import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { QRCode } from '@notify/shared';

import type { ShopWithDetails } from '../../services/admin.service';
import { adminService } from '../../services/admin.service';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | Date): string {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function subStatusBadge(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700';
    case 'trial':
      return 'bg-blue-100 text-blue-700';
    case 'expired':
      return 'bg-red-100 text-red-700';
    case 'grace':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

// ─── Skeleton components ──────────────────────────────────────────────────────

function StatCardSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-3 h-4 w-24 animate-pulse rounded bg-gray-200" />
      <div className="h-8 w-16 animate-pulse rounded bg-gray-200" />
    </div>
  );
}

function TableRowSkeleton() {
  return (
    <tr>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
        </td>
      ))}
    </tr>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number;
  color: string;
}

function StatCard({ label, value, color }: StatCardProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-medium text-[#64748B]">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <svg
        className="mb-4 h-16 w-16 text-gray-300"
        viewBox="0 0 64 64"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <rect x="8" y="16" width="48" height="36" rx="4" />
        <path d="M8 24h48M24 16v-4a8 8 0 0116 0v4" />
      </svg>
      <h3 className="text-base font-semibold text-[#0F172A]">No shops yet</h3>
      <p className="mt-1 text-sm text-[#64748B]">Get started by adding your first shop.</p>
      <Link
        to="/admin/shops"
        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 transition-colors"
      >
        Add your first shop
      </Link>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const [shops, setShops] = useState<ShopWithDetails[]>([]);
  const [qrCodes, setQRCodes] = useState<QRCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const [shopsData, qrData] = await Promise.all([
          adminService.getShops(),
          adminService.getQRCodes(),
        ]);
        setShops(shopsData);
        setQRCodes(qrData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalShops = shops.length;
  const activeShops = shops.filter((s) => s.isActive).length;
  const inactiveShops = shops.filter((s) => !s.isActive).length;
  const totalQR = qrCodes.length;

  const recentShops = [...shops]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 10);

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard label="Total Shops" value={totalShops} color="text-[#0F172A]" />
            <StatCard label="Active Shops" value={activeShops} color="text-green-600" />
            <StatCard label="Inactive Shops" value={inactiveShops} color="text-red-500" />
            <StatCard label="Total QR Codes" value={totalQR} color="text-[#6366F1]" />
          </>
        )}
      </div>

      {/* Recent shops */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-[#0F172A]">Recent Shops</h2>
          <Link
            to="/admin/shops"
            className="text-sm font-medium text-[#6366F1] hover:text-indigo-700"
          >
            View all
          </Link>
        </div>

        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRowSkeleton key={i} />
                ))}
              </tbody>
            </table>
          </div>
        ) : recentShops.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-[#F8FAFC]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Shop
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Owner
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Subscription
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748B]">
                    Created
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentShops.map((shop) => (
                  <tr key={shop.id} className="hover:bg-[#F8FAFC] transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0F172A]">{shop.name}</p>
                      <p className="text-xs text-[#64748B]">{shop.location}</p>
                    </td>
                    <td className="px-4 py-3 text-[#0F172A]">{shop.ownerName}</td>
                    <td className="px-4 py-3">
                      {shop.subscription ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${subStatusBadge(
                            shop.subscription.status,
                          )}`}
                        >
                          {shop.subscription.status}
                        </span>
                      ) : (
                        <span className="text-xs text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          shop.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-600'
                        }`}
                      >
                        {shop.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#64748B]">{formatDate(shop.createdAt)}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/shops/${shop.id}`}
                        className="text-xs font-medium text-[#6366F1] hover:text-indigo-700"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
